import { CollectionIcon, FolderOpenIcon } from "@heroicons/react/outline";
import { Link, useLocation } from "react-router-dom";

import { CodeHelpModal } from "../CodeHelpModal";

import { TEMPLATE_HELP_CODE } from "./constants";
import { HistoryPageNavbarResourceLinksProps } from "./types";

import LoadingSpinner from "@/components/LoadingSpinner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { OverflowDisplayTooltip } from "@/components/ui/Tooltip/OverflowDisplayTooltip";
import { Request } from "@/types/requests";
import { keepSidebarOnlySearchParams } from "@/utils/requestLogUrlParams";

const getPromptVersionLabel = (promptVersionNumber: number | null | undefined) =>
  typeof promptVersionNumber === "number" ? `[#${promptVersionNumber}]` : "";

const PromptTemplateLink = ({
  activeWorkspaceId,
  promptId,
  promptName,
  promptVersionNumber,
}: {
  activeWorkspaceId: number | null | undefined;
  promptId: number;
  promptName: string;
  promptVersionNumber: number | null | undefined;
}) => {
  const location = useLocation();

  if (activeWorkspaceId === null || activeWorkspaceId === undefined) {
    return null;
  }

  const promptVersionSuffix =
    typeof promptVersionNumber === "number" ? `/version/${promptVersionNumber}` : "";
  const promptVersionLabel = getPromptVersionLabel(promptVersionNumber);
  // Preserve sidebar filters only — never carry table/legacy keys that migrate into Analytics.
  const search = keepSidebarOnlySearchParams(location.search);

  return (
    <Link
      className="inline-flex min-w-0 max-w-full items-center text-blue-500 hover:text-blue-400"
      to={{
        pathname: `/workspace/${activeWorkspaceId}/prompt/${promptId}${promptVersionSuffix}`,
        search,
      }}
      title={`${promptName}${promptVersionLabel ? ` ${promptVersionLabel}` : ""}`}
    >
      <FolderOpenIcon className="mr-1 inline h-4 w-4 flex-shrink-0 align-middle" />
      <OverflowDisplayTooltip content={promptName} className="min-w-0 truncate">
        <span className="min-w-0 truncate">{promptName}</span>
      </OverflowDisplayTooltip>
      {promptVersionLabel ? <span className="ml-1 shrink-0">{promptVersionLabel}</span> : null}
    </Link>
  );
};

const RequestGroupsLink = ({
  activeWorkspaceId,
  requestGroups,
}: {
  activeWorkspaceId: number | null | undefined;
  requestGroups: Request[];
}) => {
  if (!requestGroups.length || activeWorkspaceId === null || activeWorkspaceId === undefined) {
    return null;
  }

  return (
    <Popover>
      <PopoverTrigger className="inline-flex items-center py-1 text-gray-700 hover:text-gray-500">
        <CollectionIcon className="mr-1 inline h-4 w-4 align-middle" />
        <span className="align-middle">Groups</span>
      </PopoverTrigger>
      <PopoverContent>
        <div className="divide-y divide-gray-200 text-center">
          {requestGroups.map(item => (
            <div key={item.id} className="py-2">
              <Link
                className="cursor-pointer text-blue-500 hover:text-blue-400"
                to={`/workspace/${activeWorkspaceId}/group/${item.id}`}
              >
                Group #{item.id}
              </Link>
            </div>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const HistoryPageNavbarResourceLinks = ({
  requestInfo,
  activeWorkspaceId,
  promptRegistry,
  requestGroups,
}: HistoryPageNavbarResourceLinksProps) => {
  const hasRequestGroups = requestGroups.length > 0;
  const showNoTemplateButton =
    !requestInfo?.prompt_id || (!promptRegistry.isLoading && !promptRegistry.template);
  const showResourceLinks = Boolean(
    promptRegistry.template || hasRequestGroups || promptRegistry.isLoading,
  );

  return showNoTemplateButton || showResourceLinks ? (
    <div className="flex min-w-0 max-w-full flex-wrap items-center justify-end gap-x-4 gap-y-2 overflow-hidden text-sm text-gray-800 hover:text-gray-700">
      {showNoTemplateButton ? (
        <CodeHelpModal
          buttonText="No Template"
          docsLink="https://docs.promptlayer.com/running-requests/promptlayer-run"
          title="Run a Prompt Template with PromptLayer"
          description="Use PromptLayer's run method so requests are automatically linked to a named prompt template here."
          code={TEMPLATE_HELP_CODE}
        />
      ) : null}
      {showResourceLinks ? (
        <>
          {requestInfo?.prompt_id && promptRegistry.isLoading ? <LoadingSpinner size={5} /> : null}
          {promptRegistry.template ? (
            <PromptTemplateLink
              activeWorkspaceId={activeWorkspaceId}
              promptId={promptRegistry.template.id}
              promptName={promptRegistry.template.prompt_name}
              promptVersionNumber={requestInfo.prompt_version_number}
            />
          ) : null}
          <RequestGroupsLink activeWorkspaceId={activeWorkspaceId} requestGroups={requestGroups} />
        </>
      ) : null}
    </div>
  ) : null;
};
